import { useCallback } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import IconButton from 'fe-design-base/molecules/IconButton';
import Pill from 'fe-design-base/molecules/Pill';

import {
  approveTimeOffRequest,
  deleteTimeOffRequest,
  rejectTimeOffRequest,
} from 'features/notifications/api';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';
import { STATUS_UI } from 'features/notifications/statuses';
import { RequestType, TimeOff } from 'features/notifications/types';

import { convertToTimezone, shortDayNameMonthDayFromDate } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { BaseRequest } from '../../BaseRequest';

type TimeOffStatus = keyof (typeof STATUS_UI)['time_off'];

type TimeOffProps = {
  request: TimeOff;
  setSelectedRequest?: (request: TimeOff, type: RequestType) => void;
};

const TimeOff = ({ request, setSelectedRequest }: TimeOffProps) => {
  const {
    type,
    status,
    last_status_changed_at_secs,
    requester,
    time_off,
    location,
    viewer_role,
  } = request;
  const statusValue = status as TimeOffStatus;
  const pillUI = STATUS_UI.time_off[statusValue];
  const { name: locationName } = location || {};
  const { start_at_secs, end_at_secs, category_display, duration_in_hours } =
    time_off || {};
  const { first_name, last_name, avatar } = requester || {};
  const name = `${first_name} ${last_name}`;
  const timeOffId = request.id.replace('time_off-', '');
  const timezone = (location as any)?.tz_name;

  // Convert Unix seconds to location timezone (returns milliseconds)
  const startInTz = convertToTimezone(start_at_secs, timezone);
  const endInTz = convertToTimezone(end_at_secs, timezone);

  const { isLoading: isApproving, handleAction: handleApprove } =
    useRequestAction({
      apiFunction: approveTimeOffRequest,
      itemId: timeOffId,
      successMessage: 'requests_feed.time_off.approved_success',
      initiatedFrom: 'time_off',
      buttonText: toI18n('requests_feed.actions.approve'),
    });

  const { isLoading: isRejecting, handleAction: handleReject } =
    useRequestAction({
      apiFunction: rejectTimeOffRequest,
      itemId: timeOffId,
      successMessage: 'requests_feed.time_off.rejected_success',
      initiatedFrom: 'time_off',
      buttonText: toI18n('requests_feed.actions.deny'),
    });

  const { isLoading: isDeleting, handleAction: handleDelete } =
    useRequestAction({
      apiFunction: deleteTimeOffRequest,
      itemId: timeOffId,
      successMessage: 'requests_feed.time_off.cancelled_success',
      initiatedFrom: 'time_off',
      buttonText: toI18n('requests_feed.actions.cancel_request'),
    });

  const handleClick = useCallback(() => {
    if (setSelectedRequest) {
      setSelectedRequest(request, type);
    }
  }, [setSelectedRequest, request, type]);

  return (
    <BaseRequest
      name={name}
      updatedAt={last_status_changed_at_secs}
      type={type}
      avatar={avatar}
    >
      {viewer_role === 'requester' && pillUI && (
        <Pill variant={pillUI.variant}>{pillUI.label}</Pill>
      )}
      <Box bw={2} bcolor="mono300" bradiusm p={16} row alignItemsCenter>
        <Box column grow={1}>
          <Text variant="bodySmBold" color="mono700">
            {shortDayNameMonthDayFromDate(startInTz)} -{' '}
            {shortDayNameMonthDayFromDate(endInTz)}
          </Text>
          <Box row gap={4} alignItemsCenter>
            <Text variant="bodySmTight" color="mono700">
              {category_display}
            </Text>
            <Text variant="bodySmTight" color="mono700">
              •
            </Text>
            <Text
              variant="bodySmTight"
              color="mono700"
              i18n="requests_feed.time_off.hours"
              i18nProps={{ hours: duration_in_hours }}
            />
          </Box>
          <Text variant="bodySmTight" color="mono700">
            {locationName}
          </Text>
        </Box>
        <IconButton
          icon="ChevronRight"
          label={toI18n('actions.view_details')}
          onClick={handleClick}
        />
      </Box>
      {viewer_role === 'oam' && (
        <Box row gap={8} grow={1}>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleReject}
            isLoading={isRejecting}
            disabled={isApproving || isRejecting}
          >
            {toI18n('requests_feed.actions.deny')}
          </Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleApprove}
            isLoading={isApproving}
            disabled={isRejecting || isApproving}
          >
            {toI18n('requests_feed.actions.approve')}
          </Button>
        </Box>
      )}
      {viewer_role === 'requester' && statusValue === 'pending_approval' && (
        <Button
          variant="secondaryDestructive"
          fullWidth
          onClick={handleDelete}
          isLoading={isDeleting}
          disabled={isDeleting || isApproving || isRejecting}
        >
          {toI18n('requests_feed.actions.cancel_request')}
        </Button>
      )}
    </BaseRequest>
  );
};

export default TimeOff;
